import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import SearchIcon from '@mui/icons-material/Search';

import styles from './style.module.scss';

interface SearchInputProps {
  onDebouncedSearch?: (value: string) => void;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
  value?: string;
}

const SearchInput: React.FC<SearchInputProps> = ({
  onDebouncedSearch,
  placeholder,
  className,
  debounceMs = 300,
  value = "",
}) => {
  const { t } = useTranslation();

  // States
  const [inputValue, setInputValue] = useState(value);
  // Refs
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastValue = useRef(inputValue);

  const _placeholder = placeholder || t('globals.search');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value);

  // Debounce effect for onDebouncedSearch
  useEffect(() => {
    if (onDebouncedSearch && inputValue !== lastValue.current) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        onDebouncedSearch(inputValue);
        lastValue.current = inputValue;
      }, debounceMs);
    }
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [inputValue, onDebouncedSearch, debounceMs]);

  return (
    <TextField
      value={inputValue}
      fullWidth
      onChange={handleInputChange}
      placeholder={_placeholder}
      variant="outlined"
      size="small"
      className={className}
      slotProps={{
        input: {
          type: 'search',
          startAdornment: <SearchIcon className={styles.search_icon} />,
          classes: {
            input: styles.input,
          }
        },
      }}
    />
  );
};

export default SearchInput;
